import { format } from 'date-fns';
import { formatDateOnly, todayDateOnly } from './dates';
import { formatMilli, splitPackagesAndUnits } from './quantity';

/** Phase 1's Warsaw `dd.MM.yyyy HH:mm`, under the name the card uses (F-2). */
export { formatDateTime as formatInstant } from './dates';

export type Translate = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export interface DateOnlyRange {
  from: string;
  to: string;
}

export const RECORD_CARD_KEY = 'warehouse.recordCard';
export const RECORD_CARD_PDF_KEY = `${RECORD_CARD_KEY}.pdf`;
export const EMPTY_CELL = '-';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MINUS = '−';

export function formatCardDate(value: string | null | undefined): string {
  return formatDateOnly(value);
}

export function currentWarsawYear(now: Date = new Date()): number {
  return Number(todayDateOnly(now).slice(0, 4));
}

export function yearRange(year: number): DateOnlyRange {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/** A picked calendar day; the date picker's value is local wall time. */
export function toDateOnly(date: Date | null): string {
  return date && !Number.isNaN(date.getTime())
    ? format(date, 'yyyy-MM-dd')
    : '';
}

export function isRangeValid(range: DateOnlyRange): boolean {
  return (
    DATE_ONLY_PATTERN.test(range.from) &&
    DATE_ONLY_PATTERN.test(range.to) &&
    range.from <= range.to
  );
}

/** OTHER prints the snapshot's own label (contract note 6); the generic one only when it is empty. */
export function unitLabel(
  unit: string,
  unitCustom: string | null,
  t: Translate,
): string {
  return unit === 'OTHER' && unitCustom
    ? unitCustom
    : t(`${RECORD_CARD_PDF_KEY}.units.${unit}`);
}

export function shortReference(id: string): string {
  return id.slice(-8).toUpperCase();
}

export function visitReference(
  patientName: string | null,
  visitDate: string | null,
  visitId: string,
  t: Translate,
): string {
  return t(`${RECORD_CARD_PDF_KEY}.visitRef`, {
    patient: patientName ?? EMPTY_CELL,
    date: formatCardDate(visitDate),
    id: shortReference(visitId),
  });
}

/**
 * Art. 103(3)(e) Reg. 2019/6: a handed-over drug's recipient with the address
 * on the next line; a row recorded before the address existed has the name only.
 */
export function recipientWithAddress(
  name: string | null,
  address: string | null,
): string {
  return [name, address].filter(Boolean).join('\n');
}

/** The card's document title: the file name without its extension. */
export function recordCardTitle(
  productName: string,
  range: DateOnlyRange,
  t: Translate,
): string {
  const safeName = productName
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const period = `${formatCardDate(range.from)}-${formatCardDate(range.to)}`;
  return `${t(`${RECORD_CARD_PDF_KEY}.fileName`)} ${safeName} ${period}`;
}

export function recordCardFileName(
  productName: string,
  range: DateOnlyRange,
  t: Translate,
): string {
  return `${recordCardTitle(productName, range, t)}.pdf`;
}

/**
 * The sign of `quantityMilli` applies to the whole, so a negative mixed
 * quantity reads "−(1 op. + 2 tabl.)", never "−1 op. + 2 tabl." (Q-4). A pack
 * of one prints units only, like Phase 1's `formatStock`.
 */
export function formatCardQuantity(
  quantityMilli: number,
  unitsPerPackage: number,
  unit: string,
  t: Translate,
): string {
  const magnitude = Math.abs(quantityMilli);
  const { packages, unitsMilli } =
    unitsPerPackage > 1
      ? splitPackagesAndUnits(magnitude, unitsPerPackage)
      : { packages: 0, unitsMilli: magnitude };
  const units = formatMilli(unitsMilli);
  const isMixed = packages > 0 && unitsMilli > 0;
  let text: string;
  if (isMixed) {
    text = t(`${RECORD_CARD_PDF_KEY}.quantity`, { packages, units, unit });
  } else if (packages > 0) {
    text = t(`${RECORD_CARD_PDF_KEY}.packagesOnly`, { packages });
  } else {
    text = t(`${RECORD_CARD_PDF_KEY}.unitsOnly`, { units, unit });
  }
  if (quantityMilli >= 0) {
    return text;
  }
  return isMixed ? `${MINUS}(${text})` : `${MINUS}${text}`;
}
